import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Robot, Sparkle, ShieldWarning, Quotes, Flask, PaperPlaneTilt, Plus, Plug, ChartLineUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProvidersTab from "@/pages/ai/ProvidersTab";
import UsageTab from "@/pages/ai/UsageTab";

export default function AIStudio() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [providers, setProviders] = useState([]);
  const [bases, setBases] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [shadowRuns, setShadowRuns] = useState([]);

  // agent state
  const [agent, setAgent] = useState({ kb_id: "", provider: "openai", query: "" });
  const [answer, setAnswer] = useState(null);
  const [running, setRunning] = useState(false);
  // prompt state
  const [prompt, setPrompt] = useState({ key: "", template: "" });
  // shadow state
  const [shadow, setShadow] = useState({ prompt: "", providers: ["openai"] });
  const [shadowResult, setShadowResult] = useState(null);

  const load = () => {
    api.get("/ai/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/knowledge/bases").then((r) => setBases(r.data)).catch(() => {});
    api.get("/ai/prompts").then((r) => setPrompts(r.data)).catch(() => {});
    api.get("/ai/research/shadow").then((r) => setShadowRuns(r.data)).catch(() => {});
  };
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  // The Agent provider is the org's GLOBAL LLM provider (server-side single source of truth,
  // shared with "Prepare for Etsy" and AI Ranking). Restore on load, autosave on change.
  useEffect(() => {
    if (!currentOrg) return;
    api.get("/ai/providers/selected").then(({ data }) => {
      if (data && data.provider) setAgent((a) => ({ ...a, provider: data.provider }));
    }).catch(() => {});
  }, [currentOrg]);

  const selectProvider = (v) => {
    setAgent((a) => ({ ...a, provider: v }));
    api.put("/ai/providers/selected", { provider: v }).catch(() => {});
  };

  const runAgent = async () => {
    setRunning(true); setAnswer(null);
    try {
      const { data } = await api.post("/ai/agent/run", agent);
      const runId = data.run_id;
      // Poll for the result — keeps every request fast (no 504 even when ChatGPT is slow).
      const started = Date.now();
      while (Date.now() - started < 210000) {
        await new Promise((r) => setTimeout(r, 1200));
        const { data: st } = await api.get(`/ai/agent/run/${runId}`);
        if (st.status === "done") { setAnswer(st.result); return; }
        if (st.status === "error") { toast.error(st.error || "Agent run failed"); return; }
      }
      toast.error("Agent run timed out.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setRunning(false); }
  };

  const createPrompt = async () => {
    try { await api.post("/ai/prompts", prompt); setPrompt({ key: "", template: "" }); load(); toast.success("Prompt version saved"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const runShadow = async () => {
    try { const { data } = await api.post("/ai/research/shadow", shadow); setShadowResult(data); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleProvider = (p) => {
    setShadow((s) => ({ ...s, providers: s.providers.includes(p) ? s.providers.filter((x) => x !== p) : [...s.providers, p] }));
  };

  return (
    <div>
      <PageHeader title="AI Kernel" subtitle="Multi-provider agent with Knowledge RAG, pluggable guardrails, cost tracking & shadow-mode evaluation." />

      <Tabs defaultValue="agent">
        <TabsList className="rounded-sm">
          <TabsTrigger value="agent" className="rounded-sm" data-testid="ai-tab-agent"><Robot size={15} className="mr-1" />Agent</TabsTrigger>
          <TabsTrigger value="providers" className="rounded-sm" data-testid="ai-tab-providers"><Plug size={15} className="mr-1" />Providers</TabsTrigger>
          <TabsTrigger value="usage" className="rounded-sm" data-testid="ai-tab-usage"><ChartLineUp size={15} className="mr-1" />Usage &amp; Spend</TabsTrigger>
          <TabsTrigger value="prompts" className="rounded-sm" data-testid="ai-tab-prompts">Prompts</TabsTrigger>
          <TabsTrigger value="research" className="rounded-sm" data-testid="ai-tab-research"><Flask size={15} className="mr-1" />Research</TabsTrigger>
        </TabsList>

        {/* PROVIDERS — AI Kernel enterprise infrastructure (SPRINT-108.1) */}
        <TabsContent value="providers">
          <ProvidersTab />
        </TabsContent>

        {/* USAGE & SPEND — AI governance/observability (SPRINT-108.6) */}
        <TabsContent value="usage">
          <UsageTab />
        </TabsContent>

        {/* AGENT */}
        <TabsContent value="agent" className="mt-4">
          <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            <div className="bg-card border border-border rounded-sm p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Knowledge Base</Label>
                  <Select value={agent.kb_id} onValueChange={(v) => setAgent({ ...agent, kb_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="agent-kb-select"><SelectValue placeholder="Select KB (RAG source)" /></SelectTrigger>
                    <SelectContent>{bases.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select value={agent.provider} onValueChange={selectProvider}>
                    <SelectTrigger className="rounded-sm font-mono" data-testid="agent-provider-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{providers.map((p) => (
                      <SelectItem key={p.provider} value={p.provider} className="font-mono" data-testid={`agent-provider-option-${p.provider}`}>
                        {p.kind === "browser_bridge" ? "chatgpt · browser" : `${p.provider} · ${p.model}`}
                      </SelectItem>
                    ))}</SelectContent>
                  </Select>
                  {(() => {
                    const bp = providers.find((p) => p.provider === agent.provider && p.kind === "browser_bridge");
                    if (!bp) return null;
                    return (
                      <div className="flex items-center gap-1.5 text-[11px] font-mono" data-testid="agent-provider-bridge-status">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${bp.available ? "bg-emerald-500" : "bg-amber-500"}`} />
                        <span className={bp.available ? "text-emerald-600" : "text-amber-600"}>
                          ChatGPT Browser: {bp.available ? "connected" : `unavailable${bp.reason ? ` (${bp.reason})` : ""}`}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Ask the agent</Label>
                <Textarea value={agent.query} onChange={(e) => setAgent({ ...agent, query: e.target.value })} className="rounded-sm min-h-24" placeholder="e.g. What is our brand tone of voice?" data-testid="agent-query-input" />
              </div>
              <Button onClick={runAgent} disabled={running || !agent.query.trim()} className="rounded-sm" data-testid="agent-run-button">
                <PaperPlaneTilt size={16} className="mr-2" />{running ? (agent.provider === "chatgpt_browser" ? "Waiting for ChatGPT…" : "Thinking…") : "Run agent"}
              </Button>
              {running && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono" data-testid="agent-running-indicator">
                  <span className="inline-block w-3 h-3 rounded-full border-2 border-muted-foreground border-t-transparent animate-spin" />
                  {agent.provider === "chatgpt_browser" ? "Routing through your ChatGPT browser session…" : "Running…"}
                </div>
              )}

              {answer && (
                <div className="border-t border-border pt-4 space-y-3" data-testid="agent-answer">
                  {answer.blocked ? (
                    <div className="flex items-center gap-2 text-destructive"><ShieldWarning size={18} />Blocked by input guardrails</div>
                  ) : (
                    <>
                      <div className="text-sm leading-relaxed whitespace-pre-wrap">{answer.answer}</div>
                      {answer.citations?.length > 0 && (
                        <div className="flex flex-wrap gap-1 items-center">
                          <Quotes size={14} className="text-muted-foreground" />
                          {answer.citations.map((c, i) => <Badge key={i} variant="outline" className="rounded-sm font-mono text-[10px]">{c.source} · {c.score}</Badge>)}
                        </div>
                      )}
                      <div className="flex gap-2 text-xs text-muted-foreground font-mono">
                        <Badge variant="secondary" className="rounded-sm">{answer.provider}/{answer.model}</Badge>
                        <Badge variant="secondary" className="rounded-sm">tokens {answer.usage?.tokens_in}+{answer.usage?.tokens_out}</Badge>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Providers + guardrails side panel */}
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-sm p-5">
                <div className="overline text-muted-foreground mb-3">LLM Providers</div>
                {providers.map((p) => (
                  <div key={p.provider} className="flex items-center justify-between py-1.5 border-b border-border last:border-0" data-testid={`provider-${p.provider}`}>
                    <span className="font-mono text-xs">{p.provider}</span>
                    <Badge variant="outline" className="rounded-sm text-[10px]">{p.model}</Badge>
                  </div>
                ))}
              </div>
              <div className="bg-card border border-border rounded-sm p-5">
                <div className="overline text-muted-foreground mb-3">Active Guardrails</div>
                {["guardrail.pii", "guardrail.prompt_injection", "guardrail.content_policy"].map((g) => (
                  <div key={g} className="flex items-center gap-2 py-1 text-xs" data-testid={`guardrail-${g}`}><Sparkle size={12} className="text-primary" /><span className="font-mono">{g}</span></div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* PROMPTS */}
        <TabsContent value="prompts" className="mt-4 space-y-4">
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="grid md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
              <div className="space-y-2"><Label>Key</Label><Input value={prompt.key} onChange={(e) => setPrompt({ ...prompt, key: e.target.value })} className="rounded-sm font-mono" data-testid="prompt-key-input" /></div>
              <div className="space-y-2"><Label>Template</Label><Input value={prompt.template} onChange={(e) => setPrompt({ ...prompt, template: e.target.value })} className="rounded-sm" data-testid="prompt-template-input" /></div>
              <Button onClick={createPrompt} disabled={!prompt.key.trim() || !prompt.template.trim()} className="rounded-sm" data-testid="create-prompt-button"><Plus size={16} className="mr-1" />Save version</Button>
            </div>
          </div>
          <div className="bg-card border border-border rounded-sm overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Key</TableHead><TableHead>Versions</TableHead><TableHead>Latest template</TableHead></TableRow></TableHeader>
              <TableBody>
                {prompts.map((p) => (
                  <TableRow key={p.key} data-testid={`prompt-row-${p.key}`}>
                    <TableCell className="font-mono text-xs">{p.key}</TableCell>
                    <TableCell><Badge variant="secondary" className="rounded-sm font-mono text-[10px]">v{p.versions}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground truncate max-w-md">{p.template}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* RESEARCH / SHADOW */}
        <TabsContent value="research" className="mt-4 space-y-4">
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <div className="space-y-2"><Label>Shadow-mode prompt (multi-provider comparison, no user impact)</Label>
              <Textarea value={shadow.prompt} onChange={(e) => setShadow({ ...shadow, prompt: e.target.value })} className="rounded-sm" placeholder="Prompt to evaluate across providers" data-testid="shadow-prompt-input" /></div>
            <div className="flex flex-wrap gap-2">
              {providers.map((p) => (
                <button key={p.provider} onClick={() => toggleProvider(p.provider)} data-testid={`shadow-provider-${p.provider}`}
                  className={`text-xs font-mono px-3 py-1.5 rounded-sm border ${shadow.providers.includes(p.provider) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>{p.provider}</button>
              ))}
            </div>
            <Button onClick={runShadow} disabled={!shadow.prompt.trim() || shadow.providers.length === 0} className="rounded-sm" data-testid="run-shadow-button"><Flask size={16} className="mr-2" />Run shadow evaluation</Button>
          </div>
          {shadowResult && (
            <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid="shadow-result">
              <Table>
                <TableHeader><TableRow><TableHead>Provider</TableHead><TableHead>OK</TableHead><TableHead>Latency</TableHead><TableHead>Preview / Error</TableHead></TableRow></TableHeader>
                <TableBody>
                  {shadowResult.results.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{r.provider}</TableCell>
                      <TableCell><Badge variant={r.ok ? "default" : "destructive"} className="rounded-sm text-[10px]">{String(r.ok)}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{r.latency_ms}ms</TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate max-w-md">{r.preview || r.error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
